import React, { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';
import { OptimizationService } from '../services/optimizationService';
import { OptimizationParameters, OptimizationResults, OptimizationResultsWithStats, ConvergenceData, ChartDataPoint } from '../types';
import { saveResultsToHistory } from '../utils/historyStorage';
import { getSharedBenchmarkParameters } from '../utils/parameterUtils';
import { getPipelineAlgorithmNames } from '../utils/algorithmRegistry';
import { DEFAULT_ENERGY_AWARE_WEIGHTS } from '../utils/fitnessCalculator';
import { exportAlgorithmTraceToExcel } from '../utils/exportUtils';

export interface NotificationState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

interface OptimizationContextType {
  isRunning: boolean;
  results: OptimizationResultsWithStats | null;
  convergenceData: ConvergenceData | null;
  progress: number;
  currentAlgorithm: string;
  currentIteration: number;
  totalIterations: number;
  currentRun: number;
  totalRuns: number;
  completedAlgorithms: Set<string>;
  liveConvergence: Record<string, ChartDataPoint[]>;
  notification: NotificationState | null;
  startOptimization: (params: OptimizationParameters, datasetText: string) => Promise<void>;
  startCompleteRun: (
    baseParams: OptimizationParameters,
    datasetText: string,
    workflowStart: number,
    workflowEnd: number,
    increment: number,
    numberOfRuns: number,
    onProgress?: (current: number, total: number, taskCount: number, params?: OptimizationParameters) => void
  ) => Promise<Array<{ taskCount: number; results: OptimizationResults; timestamp: string }>>;
  cancelOptimization: () => void;
  clearResults: () => void;
  showNotification: (message: string, severity?: 'success' | 'error' | 'warning' | 'info') => void;
  hideNotification: () => void;
}

const OptimizationContext = createContext<OptimizationContextType | undefined>(undefined);

export const useOptimization = () => {
  const context = useContext(OptimizationContext);
  if (!context) {
    throw new Error('useOptimization must be used within OptimizationProvider');
  }
  return context;
};

function parseAlgorithmLabel(label: string): {
  algorithmName: string;
  runNumber?: number;
  totalRuns?: number;
} {
  const runMatch = label.match(/^(.*)\s+\(Run\s+(\d+)\/(\d+)\)$/);
  if (!runMatch) {
    return { algorithmName: label.trim() };
  }

  return {
    algorithmName: runMatch[1].trim(),
    runNumber: Number(runMatch[2]),
    totalRuns: Number(runMatch[3]),
  };
}

function createProgressMap(algorithmNames: string[]): Map<string, number> {
  return new Map(algorithmNames.map((name) => [name, 0]));
}

function calculateAverageAlgorithmProgress(
  algorithmNames: string[],
  progressMap: Map<string, number>
): number {
  if (algorithmNames.length === 0) {
    return 0;
  }

  const total = algorithmNames.reduce(
    (sum, algorithmName) => sum + (progressMap.get(algorithmName) ?? 0),
    0
  );

  return total / algorithmNames.length;
}

function formatCurrentAlgorithmLabel(
  activeAlgorithms: Set<string>,
  parallelExecution: boolean,
  prefix?: string
): string {
  const activeList = Array.from(activeAlgorithms);
  if (activeList.length === 0) {
    return '';
  }

  const label = parallelExecution && activeList.length > 1
    ? activeList.join(', ')
    : activeList[activeList.length - 1];

  return prefix ? `${prefix}${label}` : label;
}

function waitForUiPaint(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame !== 'function') {
      setTimeout(resolve, 0);
      return;
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });
}

interface OptimizationProviderProps {
  children: ReactNode;
}

export const OptimizationProvider: React.FC<OptimizationProviderProps> = ({ children }) => {
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState<OptimizationResultsWithStats | null>(null);
  const [convergenceData, setConvergenceData] = useState<ConvergenceData | null>(null);
  const [progress, setProgress] = useState(0);
  const [currentAlgorithm, setCurrentAlgorithm] = useState('');
  const [currentIteration, setCurrentIteration] = useState(0);
  const [totalIterations, setTotalIterations] = useState(0);
  const [currentRun, setCurrentRun] = useState(0);
  const [totalRuns, setTotalRuns] = useState(1);
  const [completedAlgorithms, setCompletedAlgorithms] = useState<Set<string>>(new Set());
  const [liveConvergence, setLiveConvergence] = useState<Record<string, ChartDataPoint[]>>({});
  const [notification, setNotification] = useState<NotificationState | null>(null);
  const [service] = useState(() => new OptimizationService());
  const cancelFlagRef = useRef(false);

  const resetLiveConvergence = useCallback(() => {
    setLiveConvergence({});
  }, []);

  const appendLiveConvergencePoint = useCallback(
    (algorithm: string, point: ChartDataPoint) => {
      setLiveConvergence((prev) => {
        const next = { ...prev };
        const existing = next[algorithm] ? [...next[algorithm]] : [];
        existing.push(point);
        next[algorithm] = existing;
        return next;
      });
    },
    []
  );

  const showNotification = useCallback((message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setNotification({ open: true, message, severity });
  }, []);

  const hideNotification = useCallback(() => {
    setNotification(null);
  }, []);

  const startOptimization = useCallback(async (
    params: OptimizationParameters,
    datasetText: string
  ) => {
    // Reset cancel flag when starting new optimization
    cancelFlagRef.current = false;
    setIsRunning(true);
    setProgress(0);
    setResults(null);
    setConvergenceData(null);
    resetLiveConvergence();
    setCurrentAlgorithm('');
    setCurrentIteration(0);
    setTotalIterations(params.iterations);
    setCurrentRun(0);
    setTotalRuns(params.numberOfRuns || 1);
    setCompletedAlgorithms(new Set());

    try {
      const includeSflaGaBaseline = params.includeSflaGaBaseline ?? false;
      const pipelineAlgorithmNames = getPipelineAlgorithmNames(includeSflaGaBaseline);
      const parallelExecution = params.parallelExecution ?? false;
      const numberOfRuns = (params.numberOfRuns && params.numberOfRuns > 1) ? 5 : (params.numberOfRuns || 1);
      const useStatistics = numberOfRuns > 1;
      const progressByAlgorithm = createProgressMap(pipelineAlgorithmNames);
      const activeAlgorithms = new Set<string>();
      let activeStatisticsRun = 1;
      let lastInitializedRun = 1;

      setTotalRuns(numberOfRuns);

      const getRunPrefix = () =>
        useStatistics ? `Run ${activeStatisticsRun}/${numberOfRuns}: ` : undefined;

      const updateCurrentAlgorithmState = () => {
        setCurrentAlgorithm(
          formatCurrentAlgorithmLabel(activeAlgorithms, parallelExecution, getRunPrefix())
        );
      };

      const updateOverallProgress = () => {
        const averageProgress = calculateAverageAlgorithmProgress(
          pipelineAlgorithmNames,
          progressByAlgorithm
        );

        if (useStatistics) {
          const completedRuns = Math.max(0, activeStatisticsRun - 1);
          setProgress(Math.min(100, ((completedRuns + averageProgress) / numberOfRuns) * 100));
          return;
        }

        setProgress(Math.min(100, averageProgress * 100));
      };

      const resetStatisticsRunTracking = (runNumber: number) => {
        progressByAlgorithm.clear();
        pipelineAlgorithmNames.forEach((algorithmName) => {
          progressByAlgorithm.set(algorithmName, 0);
        });
        activeAlgorithms.clear();
        setCompletedAlgorithms(new Set());
        resetLiveConvergence();
        activeStatisticsRun = runNumber;
        lastInitializedRun = runNumber;
        setCurrentRun(runNumber);
      };

      // Load dataset with fitness mode and weights
      const fitnessMode = params.fitnessMode ?? 'energy-aware';
      const energyAwareWeights = params.energyAwareWeights ?? DEFAULT_ENERGY_AWARE_WEIGHTS;
      service.loadDataset(
        datasetText,
        params.taskCount,
        fitnessMode,
        energyAwareWeights,
        params.seed,
        params.taskSpecificVariationEnabled ?? true
      );

      const result = useStatistics
        ? await service.runOptimizationWithStatistics(
            params,
            (algorithmLabel) => {
              if (cancelFlagRef.current) {
                return;
              }

              const { algorithmName, runNumber } = parseAlgorithmLabel(algorithmLabel);
              if (runNumber !== undefined) {
                activeStatisticsRun = runNumber;
                setCurrentRun(runNumber);

                if (runNumber !== lastInitializedRun) {
                  resetStatisticsRunTracking(runNumber);
                }
              }

              activeAlgorithms.add(algorithmName);
              setLiveConvergence((prev) => ({ ...prev, [algorithmName]: prev[algorithmName] ?? [] }));
              progressByAlgorithm.set(
                algorithmName,
                Math.max(progressByAlgorithm.get(algorithmName) ?? 0, 0)
              );
              setCurrentIteration(0);
              updateCurrentAlgorithmState();
              updateOverallProgress();
            },
            (algorithm, generation, fitness, point) => {
              if (cancelFlagRef.current) {
                return;
              }

              activeAlgorithms.add(algorithm);
              progressByAlgorithm.set(
                  algorithm,
                  Math.max(progressByAlgorithm.get(algorithm) ?? 0, generation / params.iterations)
                );
              appendLiveConvergencePoint(algorithm, point ?? { iteration: generation, fitness });
              setCurrentIteration(generation);
              updateCurrentAlgorithmState();
              updateOverallProgress();
            },
            (runNumber, totalRuns, intermediateResults) => {
              if (!cancelFlagRef.current) {
                setCurrentRun(runNumber);
                // Update UI with intermediate results after each statistical run (PROGRESSIVE UPDATE)
                if (intermediateResults) {
                  const timestamp = new Date().toISOString();
                  setResults({
                    ...intermediateResults,
                    timestamp,
                  });
                }
              }
            },
            (algorithm, result, partialResults) => {
              // Update UI with partial results as each algorithm completes (same as complete run)
              if (!cancelFlagRef.current) {
                activeAlgorithms.delete(algorithm);
                progressByAlgorithm.set(algorithm, 1);
                updateCurrentAlgorithmState();
                updateOverallProgress();

                // Mark algorithm as completed
                setCompletedAlgorithms(prev => {
                  const newSet = new Set(prev);
                  newSet.add(algorithm);
                  return newSet;
                });
                
                // For statistics mode, only update results on the last run to show final statistics
                // But we can still show partial results during the last run
                // We'll track this by checking if we're on the last run via currentRun state
                // For now, update results progressively - final statistics will be set at the end
                setResults(prev => {
                  if (!prev) {
                    return {
                      ...partialResults,
                      configuration: {
                        taskCount: params.taskCount,
                        propertyCount: params.qosProperties,
                        initialPopulation: params.initialPopulation,
                        crossover: params.crossoverRate,
                        mutation: params.mutationRate,
                        includeSflaGaBaseline: params.includeSflaGaBaseline,
                        parallelExecution: params.parallelExecution,
                        recordMode: params.recordMode,
                        fitnessMode: params.fitnessMode,
                        energyAwareWeights: params.energyAwareWeights,
                        seed: params.seed,
                        taskSpecificVariationEnabled: params.taskSpecificVariationEnabled,
                      },
                    } as OptimizationResultsWithStats;
                  }
                  return {
                    ...prev,
                    ...partialResults,
                  } as OptimizationResultsWithStats;
                });
              }
            },
            () => cancelFlagRef.current // shouldCancel callback
          )
        : await service.runOptimization(
            params,
            (algorithm) => {
              if (!cancelFlagRef.current) {
                activeAlgorithms.add(algorithm);
                setLiveConvergence((prev) => ({ ...prev, [algorithm]: prev[algorithm] ?? [] }));
                progressByAlgorithm.set(
                  algorithm,
                  Math.max(progressByAlgorithm.get(algorithm) ?? 0, 0)
                );
                setCurrentIteration(0);
                updateCurrentAlgorithmState();
                updateOverallProgress();
              }
            },
            (algorithm, generation, fitness, point) => {
              if (!cancelFlagRef.current) {
                activeAlgorithms.add(algorithm);
                progressByAlgorithm.set(
                  algorithm,
                  Math.max(progressByAlgorithm.get(algorithm) ?? 0, generation / params.iterations)
                );
                appendLiveConvergencePoint(algorithm, point ?? { iteration: generation, fitness });
                setCurrentIteration(generation);
                updateCurrentAlgorithmState();
                updateOverallProgress();
              }
            },
            (algorithm, result, partialResults) => {
              // Update UI with partial results as each algorithm completes (same as complete run)
              if (!cancelFlagRef.current) {
                activeAlgorithms.delete(algorithm);
                progressByAlgorithm.set(algorithm, 1);
                updateCurrentAlgorithmState();
                updateOverallProgress();

                // Mark algorithm as completed
                setCompletedAlgorithms(prev => {
                  const newSet = new Set(prev);
                  newSet.add(algorithm);
                  return newSet;
                });
                
                // Update results progressively with partial results
                setResults(prev => {
                  if (!prev) {
                    // Initialize with partial results and configuration
                    return {
                      ...partialResults,
                      configuration: {
                        taskCount: params.taskCount,
                        propertyCount: params.qosProperties,
                        initialPopulation: params.initialPopulation,
                        crossover: params.crossoverRate,
                        mutation: params.mutationRate,
                        includeSflaGaBaseline: params.includeSflaGaBaseline,
                        parallelExecution: params.parallelExecution,
                        recordMode: params.recordMode,
                        fitnessMode: params.fitnessMode,
                        energyAwareWeights: params.energyAwareWeights,
                        seed: params.seed,
                        taskSpecificVariationEnabled: params.taskSpecificVariationEnabled,
                      },
                    } as OptimizationResultsWithStats;
                  }
                  // Merge with existing results
                  return {
                    ...prev,
                    ...partialResults,
                  } as OptimizationResultsWithStats;
                });
              }
            },
            () => cancelFlagRef.current // shouldCancel callback
          );

      if (!cancelFlagRef.current) {
        const timestamp = new Date().toISOString();
        
        // Update UI with final results and convergence data (same as complete run)
        // Results may already be partially set from progressive updates, so merge with final results
        if (useStatistics) {
          // For statistics mode, result.results is OptimizationResultsWithStats
          setResults({
            ...(result.results as OptimizationResultsWithStats),
            timestamp,
          });
        } else {
          // For single run, result.results is OptimizationResults
          setResults({
            ...result.results,
            timestamp,
          } as OptimizationResultsWithStats);
        }
        setConvergenceData(result.convergenceData);
        setProgress(100);
        setCurrentAlgorithm('');
        setCurrentIteration(0);
        setTotalIterations(0);
        setCurrentRun(0);
        // Mark all algorithms as completed for final display
        setCompletedAlgorithms(new Set(pipelineAlgorithmNames));

        if (params.recordMode && params.recordMode !== 'off' && result.traceRecords?.length) {
          const traceTimestamp = timestamp.replace(/[:.]/g, '-').slice(0, -5);
          const recordModeLabel = params.recordMode === 'full' ? 'full trace' : 'summary';
          setCurrentAlgorithm(`Exporting ${recordModeLabel} algorithm steps`);
          setProgress(100);
          showNotification(
            `Exporting ${recordModeLabel} algorithm steps to Excel...`,
            'info'
          );
          await waitForUiPaint();

          exportAlgorithmTraceToExcel(
            result.traceRecords,
            `algorithm_steps_${params.recordMode}_${traceTimestamp}.xlsx`
          );
          setCurrentAlgorithm('');
          showNotification(
            `${params.recordMode === 'full' ? 'Full' : 'Summary'} algorithm steps exported`,
            'success'
          );
        }
        
        // Save to history
        try {
          saveResultsToHistory({
            ...(result.results as OptimizationResultsWithStats),
            timestamp,
          });
        } catch (error) {
          console.warn('Failed to save results to history:', error);
        }
      }
    } catch (error) {
      // Don't show error notification if optimization was cancelled
      if (cancelFlagRef.current) {
        console.log('Optimization cancelled by user');
      } else {
        console.error('Optimization error:', error);
        showNotification(
          'Optimization failed: ' + (error instanceof Error ? error.message : String(error)),
          'error'
        );
      }
    } finally {
      if (!cancelFlagRef.current) {
        setIsRunning(false);
        setCurrentAlgorithm('');
        setCurrentRun(0);
      }
    }
  }, [appendLiveConvergencePoint, resetLiveConvergence, service, showNotification]);

  const startCompleteRun = useCallback(async (
    baseParams: OptimizationParameters,
    datasetText: string,
    workflowStart: number,
    workflowEnd: number,
    increment: number,
    numberOfRuns: number,
    onProgress?: (current: number, total: number, taskCount: number, params?: OptimizationParameters) => void
  ): Promise<Array<{ taskCount: number; results: OptimizationResults; timestamp: string }>> => {
    // PERFORMANCE OPTIMIZATIONS FOR COMPLETE RUN:
    // 1. History saving is skipped during complete run to avoid localStorage bottleneck
    // 2. Heavy calculations (normalization, composite scores, Pareto analysis) are deferred until after complete run finishes
    // 3. Only final statistics are calculated per task count (required for Excel export)
    // 4. UI updates are throttled (every 5 generations instead of every generation)
    // 5. UI updates use requestAnimationFrame to batch DOM updates
    // 6. Excel export remains fully functional with all data intact (statistics are preserved)
    
    // Reset cancel flag when starting new complete run
    cancelFlagRef.current = false;
    setIsRunning(true);
    setProgress(0);
    setResults(null);
    setConvergenceData(null);
    setCurrentAlgorithm('Complete Run');
    setCurrentIteration(0);
    setTotalIterations(0);
    setCompletedAlgorithms(new Set());
    const allResults: Array<{ taskCount: number; results: OptimizationResults; timestamp: string }> = [];
    
    // Calculate task counts
    const taskCounts: number[] = [];
    for (let taskCount = workflowStart; taskCount <= workflowEnd; taskCount += increment) {
      taskCounts.push(taskCount);
    }

    const totalRuns = taskCounts.length;
    setTotalRuns(totalRuns);
    setCurrentRun(0);

    try {
      for (let i = 0; i < taskCounts.length; i++) {
        if (cancelFlagRef.current) break;

        resetLiveConvergence();

        const taskCount = taskCounts[i];
        setCurrentRun(i + 1);
        setProgress((i / totalRuns) * 100);
        setCurrentAlgorithm(`Complete Run: ${taskCount} tasks (${i + 1}/${totalRuns})`);

        // Derive the shared benchmark configuration for this task count.
        const sharedParams = getSharedBenchmarkParameters(taskCount);

        // Create parameters with the shared values for this task count.
        const params: OptimizationParameters = {
          ...baseParams,
          taskCount,
          iterations: sharedParams.iterations,
          initialPopulation: sharedParams.initialPopulation,
          selectionRate: sharedParams.selectionRate,
          crossoverRate: sharedParams.crossoverRate,
          mutationRate: sharedParams.mutationRate,
          memeplexCount: sharedParams.memeplexCount,
          numberOfRuns,
          includeSflaGaBaseline: baseParams.includeSflaGaBaseline ?? false,
          recordMode: 'off',
          parameterPolicy: sharedParams.parameterPolicy,
        };

        // Notify progress callback with current parameters for UI update
        if (onProgress) {
          onProgress(i + 1, totalRuns, taskCount, params);
        }

        // Load dataset with new task count and fitness mode/weights
        const fitnessMode = params.fitnessMode ?? 'energy-aware';
        const energyAwareWeights = params.energyAwareWeights ?? DEFAULT_ENERGY_AWARE_WEIGHTS;
        service.loadDataset(
          datasetText,
          taskCount,
          fitnessMode,
          energyAwareWeights,
          params.seed,
          params.taskSpecificVariationEnabled ?? true
        );

        // Set total iterations for this optimization run
        setTotalIterations(params.iterations);
        setCurrentIteration(0);

        // Track algorithms for this run - reset for each optimization run
        const algorithmsInOrder = getPipelineAlgorithmNames(params.includeSflaGaBaseline ?? false);
        const runCompletedAlgorithms = new Set<string>();
        const parallelExecution = params.parallelExecution ?? false;
        const progressByAlgorithm = createProgressMap(algorithmsInOrder);
        const activeAlgorithms = new Set<string>();

        // Run optimization (with statistics if multiple runs)
        const numberOfRunsForStats = params.numberOfRuns || 1;
        const useStatistics = numberOfRunsForStats > 1;
        let activeStatisticsRun = 1;
        let lastInitializedStatisticsRun = 1;

        const updateTaskCurrentAlgorithm = () => {
          const prefix = useStatistics
            ? `Complete Run: ${taskCount} tasks - Run ${activeStatisticsRun}/${numberOfRunsForStats}: `
            : `Complete Run: ${taskCount} tasks - `;
          setCurrentAlgorithm(
            formatCurrentAlgorithmLabel(activeAlgorithms, parallelExecution, prefix)
          );
        };

        const updateTaskProgress = () => {
          const averageProgress = calculateAverageAlgorithmProgress(
            algorithmsInOrder,
            progressByAlgorithm
          );
          const taskProgress = useStatistics
            ? ((activeStatisticsRun - 1) + averageProgress) / numberOfRunsForStats
            : averageProgress;

          setProgress(Math.min(100, ((i + taskProgress) / totalRuns) * 100));
        };

        const resetStatisticsRunTracking = (runNumber: number) => {
          progressByAlgorithm.clear();
          algorithmsInOrder.forEach((algorithmName) => {
            progressByAlgorithm.set(algorithmName, 0);
          });
          activeAlgorithms.clear();
          runCompletedAlgorithms.clear();
          setCompletedAlgorithms(new Set());
          resetLiveConvergence();
          activeStatisticsRun = runNumber;
          lastInitializedStatisticsRun = runNumber;
        };

        const result = useStatistics
          ? await service.runOptimizationWithStatistics(
              params,
              (algorithmLabel) => {
                if (!cancelFlagRef.current) {
                  const { algorithmName, runNumber } = parseAlgorithmLabel(algorithmLabel);
                  if (runNumber !== undefined) {
                    activeStatisticsRun = runNumber;
                    if (runNumber !== lastInitializedStatisticsRun) {
                      resetStatisticsRunTracking(runNumber);
                    }
                  }

                  activeAlgorithms.add(algorithmName);
                  setLiveConvergence((prev) => ({ ...prev, [algorithmName]: prev[algorithmName] ?? [] }));
                  progressByAlgorithm.set(
                    algorithmName,
                    Math.max(progressByAlgorithm.get(algorithmName) ?? 0, 0)
                  );
                  setCurrentIteration(0);
                  updateTaskCurrentAlgorithm();
                  updateTaskProgress();
                }
              },
              (algorithm, generation, fitness, point) => {
                if (!cancelFlagRef.current) {
                  activeAlgorithms.add(algorithm);
                  progressByAlgorithm.set(
                    algorithm,
                    Math.max(progressByAlgorithm.get(algorithm) ?? 0, generation / params.iterations)
                  );
                  appendLiveConvergencePoint(algorithm, point ?? { iteration: generation, fitness });
                  setCurrentIteration(generation);
                  updateTaskCurrentAlgorithm();
                  updateTaskProgress();
                }
              },
              () => {
                if (!cancelFlagRef.current) {
                  updateTaskProgress();
                }
              },
              (algorithm) => {
                if (!cancelFlagRef.current) {
                  activeAlgorithms.delete(algorithm);
                  progressByAlgorithm.set(algorithm, 1);
                  runCompletedAlgorithms.add(algorithm);
                  setCompletedAlgorithms(new Set(runCompletedAlgorithms));
                  updateTaskCurrentAlgorithm();
                  updateTaskProgress();
                }
              },
              () => cancelFlagRef.current, // shouldCancel callback
              true // skipHeavyCalculations: Skip normalization, composite scores, and Pareto analysis during complete run
            )
          : await service.runOptimization(
              params,
              (algorithm) => {
                if (!cancelFlagRef.current) {
                  activeAlgorithms.add(algorithm);
                  setLiveConvergence((prev) => ({ ...prev, [algorithm]: prev[algorithm] ?? [] }));
                  progressByAlgorithm.set(
                    algorithm,
                    Math.max(progressByAlgorithm.get(algorithm) ?? 0, 0)
                  );
                  setCurrentIteration(0);
                  updateTaskCurrentAlgorithm();
                  updateTaskProgress();
                }
              },
              (algorithm, generation, fitness, point) => {
                if (!cancelFlagRef.current) {
                  activeAlgorithms.add(algorithm);
                  progressByAlgorithm.set(
                    algorithm,
                    Math.max(progressByAlgorithm.get(algorithm) ?? 0, generation / params.iterations)
                  );
                  appendLiveConvergencePoint(algorithm, point ?? { iteration: generation, fitness });
                  setCurrentIteration(generation);
                  updateTaskCurrentAlgorithm();
                  updateTaskProgress();
                }
              },
              (algorithm) => {
                if (!cancelFlagRef.current) {
                  activeAlgorithms.delete(algorithm);
                  progressByAlgorithm.set(algorithm, 1);
                  runCompletedAlgorithms.add(algorithm);
                  setCompletedAlgorithms(new Set(runCompletedAlgorithms));
                  updateTaskCurrentAlgorithm();
                  updateTaskProgress();
                }
              },
              () => cancelFlagRef.current // shouldCancel callback
            );
        
        // Mark all algorithms as completed after optimization finishes
        if (!cancelFlagRef.current) {
          algorithmsInOrder.forEach(alg => runCompletedAlgorithms.add(alg));
          setCompletedAlgorithms(new Set(runCompletedAlgorithms));
        }

        if (!cancelFlagRef.current) {
          const timestamp = new Date().toISOString();
          allResults.push({
            taskCount,
            results: result.results,
            timestamp,
          });

          // Update UI with current results and convergence data in real-time
          // Use requestAnimationFrame to batch UI updates and prevent blocking
          requestAnimationFrame(() => {
            if (!cancelFlagRef.current) {
              setResults({
                ...result.results,
                timestamp,
              });
              setConvergenceData(result.convergenceData);
            }
          });

          // OPTIMIZATION: Skip history saving during complete run to avoid localStorage bottleneck
          // History will be saved only for the final result, or user can save manually
          // This significantly improves performance for large complete runs
          // try {
          //   saveResultsToHistory(result.results);
          // } catch (error) {
          //   console.warn('Failed to save results to history:', error);
          // }
        }
      }

      if (!cancelFlagRef.current) {
        setProgress(100);
        setCurrentAlgorithm('');
        setCurrentRun(0);
        setCurrentIteration(0);
        setTotalIterations(0);
        // Mark all algorithms as completed for final display
        setCompletedAlgorithms(
          new Set(getPipelineAlgorithmNames(baseParams.includeSflaGaBaseline ?? false))
        );
        // Results and convergenceData are already set after each iteration,
        // so the UI will show the last completed optimization with best results summary
        // Keep results set so best results summary is displayed
        const lastResult = allResults[allResults.length - 1];
        if (lastResult) {
          try {
            saveResultsToHistory({
              ...(lastResult.results as OptimizationResultsWithStats),
              timestamp: lastResult.timestamp,
            });
          } catch (error) {
            console.warn('Failed to save complete run result to history:', error);
          }
        }
      }
    } catch (error) {
      // Don't show error notification if complete run was cancelled
      if (cancelFlagRef.current) {
        console.log('Complete run cancelled by user');
      } else {
        console.error('Complete run error:', error);
        showNotification(
          'Complete run failed: ' + (error instanceof Error ? error.message : String(error)),
          'error'
        );
      }
    } finally {
      if (!cancelFlagRef.current) {
        setIsRunning(false);
        setCurrentAlgorithm('');
        setCurrentRun(0);
      }
    }

    return allResults;
  }, [appendLiveConvergencePoint, resetLiveConvergence, service, showNotification]);

  const cancelOptimization = useCallback(() => {
    // Set cancel flag to stop optimization loops
    cancelFlagRef.current = true;
    service.cancelActiveWork();
    setIsRunning(false);
    setProgress(0);
    setCurrentAlgorithm('');
    setCurrentIteration(0);
    setTotalIterations(0);
    setCurrentRun(0);
    setTotalRuns(1);
    setCompletedAlgorithms(new Set());
    // Clear results to hide progress bar
    setResults(null);
    setConvergenceData(null);
    resetLiveConvergence();
  }, [resetLiveConvergence, service]);

  const clearResults = useCallback(() => {
    setResults(null);
    setConvergenceData(null);
    setProgress(0);
    setCompletedAlgorithms(new Set());
    resetLiveConvergence();
  }, [resetLiveConvergence]);

  return (
    <OptimizationContext.Provider
      value={{
        isRunning,
        results,
        convergenceData,
        progress,
        currentAlgorithm,
        currentIteration,
        totalIterations,
        currentRun,
        totalRuns,
        completedAlgorithms,
        liveConvergence,
        notification,
        startOptimization,
        startCompleteRun,
        cancelOptimization,
        clearResults,
        showNotification,
        hideNotification,
      }}
    >
      {children}
    </OptimizationContext.Provider>
  );
};
